import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { MapPin, AlertTriangle, Monitor } from 'lucide-react';
import { LoginHistoryEntry } from '@/hooks/useSecurityDashboard';

interface LoginHistoryTableProps {
  history: LoginHistoryEntry[];
}

export const LoginHistoryTable = ({ history }: LoginHistoryTableProps) => {
  const parseUserAgent = (ua: string) => {
    let browser = 'Unknown';
    let os = 'Unknown';

    if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';

    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    return `${browser} on ${os}`;
  };

  const getCountryFlag = (country: string) => {
    if (country === 'Hidden' || country === 'Unknown') return '🌐';
    
    const countryFlags: Record<string, string> = {
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Canada': '🇨🇦',
      'Australia': '🇦🇺',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Japan': '🇯🇵',
      'China': '🇨🇳',
      'India': '🇮🇳',
      'Brazil': '🇧🇷',
    };
    
    return countryFlags[country] || '🌍';
  };

  if (history.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-8">
        No login history available yet
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {history.map((entry) => (
        <div
          key={entry.id}
          className={`p-4 rounded-lg border transition-colors ${
            entry.is_suspicious
              ? 'border-destructive/50 bg-destructive/5'
              : 'border-border bg-card'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              {/* Date & Time */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {format(new Date(entry.login_time), 'PPp')}
                </span>
                {entry.is_suspicious && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Suspicious
                  </Badge>
                )}
              </div>

              {/* Location */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">
                  {getCountryFlag(entry.country)} {entry.city}, {entry.country}
                </span>
              </div>

              {/* Device */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Monitor className="w-4 h-4" />
                <span>{parseUserAgent(entry.user_agent)}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
